import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { StatsGrid, StatCard, BarChartWidget, PieChartWidget, AnalyticsGrid, generateMockData } from '../components/Charts';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function PackageManager({ level = 'tenant', view = 'default' }) {
  const params = useParams();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    if (view === 'add') return level === 'saas' ? 'install' : level === 'tenant' ? 'activate' : 'add';
    if (view === 'usage') return 'usage';
    if (view === 'available') return level === 'saas' ? 'install' : 'activate';
    return 'all';
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', type: 'standard', category: 'productivity',
    version: '1.0.0', pricing: { monthlyPrice: 0, yearlyPrice: 0, perUser: false },
    features: '', limits: { maxUsers: 100, maxStorage: '1GB', apiCalls: 10000 },
    isActive: true, isFeatured: false, sortOrder: 0
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  const getIds = () => {
    switch (level) {
      case 'paas': return { id: params.paasId, type: 'saas' };
      case 'saas': return { id: params.saasId, type: 'tenant' };
      case 'tenant': return { id: params.tenantId, type: 'tenant' };
      case 'super': return { id: 'super', type: 'platform' };
      default: return { id: params.tenantId, type: 'tenant' };
    }
  };

  useEffect(() => {
    const { id } = getIds();
    if (id) fetchData();
  }, [level, params]);

  // Polling for real-time data updates
  useEffect(() => {
    const pollPackages = setInterval(() => {
      fetchData();
    }, 15000); // 15 seconds

    return () => {
      clearInterval(pollPackages);
    };
  }, []);

  const fetchData = async () => {
    const { id, type } = getIds();
    try {
      const [installedRes, availableRes] = await Promise.all([
        api.get(`/packages/${type}/${id}`),
        api.get('/packages/platform')
      ]);
      setPackages(installedRes.data.data || []);
      setAvailablePackages(availableRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async (packageId) => {
    const { id, type } = getIds();
    try {
      await api.post(`/packages/${type}/${id}`, { packageId });
      fetchData();
      setShowModal(false);
      alert('Package installed successfully!');
    } catch (error) {
      alert('Failed to install package: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleUninstall = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to uninstall this package?')) return;
    const { id, type } = getIds();
    try {
      await api.delete(`/packages/${type}/${id}/${assignmentId}`);
      fetchData();
      alert('Package uninstalled');
    } catch (error) {
      alert('Failed to uninstall package');
    }
  };

  const handleUpdateStatus = async (assignmentId, status) => {
    const { id, type } = getIds();
    try {
      await api.patch(`/packages/${type}/${id}/${assignmentId}`, { status });
      fetchData();
    } catch (error) {
      alert('Failed to update package status');
    }
  };

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      const payload = {
        ...formData,
        features: formData.features.split(',').map(f => f.trim()).filter(Boolean),
        pricing: {
          monthlyPrice: Number(formData.pricing.monthlyPrice),
          yearlyPrice: Number(formData.pricing.yearlyPrice),
          perUser: formData.pricing.perUser
        },
        limits: {
          maxUsers: Number(formData.limits.maxUsers),
          maxStorage: formData.limits.maxStorage,
          apiCalls: Number(formData.limits.apiCalls)
        }
      };
      await api.post('/packages/platform', payload);
      alert('Package created successfully!');
      setFormData({
        name: '', description: '', type: 'standard', category: 'productivity',
        version: '1.0.0', pricing: { monthlyPrice: 0, yearlyPrice: 0, perUser: false },
        features: '', limits: { maxUsers: 100, maxStorage: '1GB', apiCalls: 10000 },
        isActive: true, isFeatured: false, sortOrder: 0
      });
      setActiveTab('all');
      fetchData();
    } catch (error) {
      alert('Failed to create package: ' + (error.response?.data?.message || error.message));
    } finally {
      setFormSubmitting(false);
    }
  };

  const getTitle = () => {
    if (view === 'add') return level === 'saas' ? 'Install Package' : level === 'tenant' ? 'Activate Package' : 'Add Package';
    if (view === 'usage') return 'Package Usage';
    if (view === 'available') return level === 'saas' ? 'Install Package' : 'Activate Package';
    switch (level) {
      case 'paas': return 'All Packages';
      case 'saas': return 'All Packages';
      case 'tenant': return 'All Packages';
      case 'super': return 'All Packages';
      default: return 'All Packages';
    }
  };

  const getContext = () => {
    switch (level) {
      case 'paas': return { back: `/paas-admin/${params.paasId}`, label: 'PAAS Admin' };
      case 'saas': return { back: `/saas-admin/${params.saasId}`, label: 'SaaS Admin' };
      case 'tenant': return { back: `/client-admin/${params.tenantId}`, label: 'Client Admin' };
      case 'super': return { back: '/super-admin/dashboard', label: 'Super Admin' };
      default: return { back: '/dashboard', label: 'Dashboard' };
    }
  };

  const getActionLabel = () => {
    if (level === 'saas') return 'Install';
    if (level === 'tenant') return 'Activate';
    return 'Add Package';
  };

  const getActionDescription = () => {
    switch (level) {
      case 'paas': return 'Manage packages in the marketplace';
      case 'saas': return 'Install packages from marketplace for your clients';
      case 'tenant': return 'Activate packages for your organization';
      case 'super': return 'Manage all platform packages';
      default: return '';
    }
  };

  const ctx = getContext();

  const pkgStats = {
    total: packages.length,
    active: packages.filter(p => p.status === 'active').length,
    trial: packages.filter(p => p.status === 'pending').length
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          {/* <button onClick={() => navigate(ctx.back)} className="text-blue-600 hover:underline mb-2">
            ← Back to {ctx.label}
          </button> */}
          <h1 className="text-3xl font-bold">{getTitle()}</h1>
          <p className="text-gray-500 mt-1">{getActionDescription()}</p>
        </div>
        {(level === 'paas' || level === 'super') ? (
          <button
            onClick={() => setActiveTab('add')}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            + Add Package
          </button>
        ) : (
          <button
            onClick={() => setActiveTab(level === 'saas' ? 'install' : 'activate')}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            + {level === 'saas' ? 'Install Package' : 'Activate Package'}
          </button>
        )}
      </div>

      <div className="flex border-b mb-4">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 ${activeTab === 'all' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}
        >
          All Packages
        </button>
        {level === 'paas' || level === 'super' ? (
          <button
            onClick={() => setActiveTab('add')}
            className={`px-4 py-2 ${activeTab === 'add' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}
          >
            Add Package
          </button>
        ) : level === 'saas' ? (
          <button
            onClick={() => setActiveTab('install')}
            className={`px-4 py-2 ${activeTab === 'install' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}
          >
            Install Package
          </button>
        ) : (
          <button
            onClick={() => setActiveTab('activate')}
            className={`px-4 py-2 ${activeTab === 'activate' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}
          >
            Activate Package
          </button>
        )}
        <button
          onClick={() => setActiveTab('usage')}
          className={`px-4 py-2 ${activeTab === 'usage' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500'}`}
        >
          Package Usage
        </button>
      </div>

      <StatsGrid>
        <StatCard title={level === 'paas' ? 'Created' : level === 'saas' ? 'Installed' : 'Activated'} value={pkgStats.total} />
        <StatCard title="Active" value={pkgStats.active} color="green" />
        <StatCard title={level === 'paas' ? 'Total SaaS' : 'Clients'} value={pkgStats.total * 2} color="blue" />
        <StatCard title="Available" value={availablePackages.length} color="purple" />
      </StatsGrid>

      {activeTab === 'all' && (
        <>
          <AnalyticsGrid>
            <BarChartWidget title="Package Usage" data={generateMockData(7)} xKey="date" yKey="users" />
            <PieChartWidget title="Package Status" data={[
              { name: 'Active', value: pkgStats.active },
              { name: 'Inactive', value: pkgStats.total - pkgStats.active }
            ]} nameKey="name" valueKey="value" />
          </AnalyticsGrid>

          <div className="bg-white rounded shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Package</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Price</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Installed</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="px-4 py-4 text-center">Loading...</td></tr>
                ) : packages.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-4 text-center">No packages installed. Click "Browse Packages" to install.</td></tr>
                ) : (
                  packages.map(pkg => (
                    <tr key={pkg.assignmentId} className="border-t">
                      <td className="px-4 py-3">
                        <div className="font-medium">{pkg.packageId?.name || 'Package'}</div>
                        <div className="text-sm text-gray-500">{pkg.packageId?.description}</div>
                      </td>
                      <td className="px-4 py-3">{pkg.packageId?.type}</td>
                      <td className="px-4 py-3">${pkg.pricing?.monthlyPrice || 0}/mo</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-sm ${
                          pkg.status === 'active' ? 'bg-green-100 text-green-800' :
                          pkg.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {pkg.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">{new Date(pkg.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleUpdateStatus(pkg.assignmentId, pkg.status === 'active' ? 'suspended' : 'active')}
                          className="text-yellow-600 hover:underline mr-3"
                        >
                          {pkg.status === 'active' ? 'Suspend' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleUninstall(pkg.assignmentId)}
                          className="text-red-600 hover:underline"
                        >
                          Uninstall
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(activeTab === 'add') && (level === 'super' || level === 'paas') ? (
        <div className="bg-white p-6 rounded shadow max-w-4xl">
          <h2 className="text-xl font-bold mb-6">Create New Package</h2>
          <form onSubmit={handleCreatePackage}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., Analytics Pro" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                <input type="text" value={formData.version} onChange={e => setFormData({...formData, version: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="1.0.0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="productivity">Productivity</option>
                  <option value="communication">Communication</option>
                  <option value="analytics">Analytics</option>
                  <option value="storage">Storage</option>
                  <option value="security">Security</option>
                  <option value="integration">Integration</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                  rows={3} className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Describe what this package does..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($)</label>
                <input type="number" min="0" step="0.01" value={formData.pricing.monthlyPrice}
                  onChange={e => setFormData({...formData, pricing: {...formData.pricing, monthlyPrice: e.target.value}})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price ($)</label>
                <input type="number" min="0" step="0.01" value={formData.pricing.yearlyPrice}
                  onChange={e => setFormData({...formData, pricing: {...formData.pricing, yearlyPrice: e.target.value}})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
                <input type="number" min="1" value={formData.limits.maxUsers}
                  onChange={e => setFormData({...formData, limits: {...formData.limits, maxUsers: e.target.value}})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Calls Limit</label>
                <input type="number" min="1" value={formData.limits.apiCalls}
                  onChange={e => setFormData({...formData, limits: {...formData.limits, apiCalls: e.target.value}})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Storage Limit</label>
                <input type="text" value={formData.limits.maxStorage}
                  onChange={e => setFormData({...formData, limits: {...formData.limits, maxStorage: e.target.value}})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="1GB" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" min="0" value={formData.sortOrder}
                  onChange={e => setFormData({...formData, sortOrder: Number(e.target.value)})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Features (comma-separated)</label>
                <input type="text" value={formData.features} onChange={e => setFormData({...formData, features: e.target.value})}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Dashboard, Reports, Alerts" />
              </div>
              <div className="md:col-span-2 flex gap-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive}
                    onChange={e => setFormData({...formData, isActive: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded" />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isFeatured}
                    onChange={e => setFormData({...formData, isFeatured: e.target.checked})}
                    className="w-4 h-4 text-indigo-600 rounded" />
                  <span className="text-sm text-gray-700">Featured</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex gap-4">
              <button type="submit" disabled={formSubmitting}
                className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 disabled:bg-gray-400">
                {formSubmitting ? 'Creating...' : 'Create Package'}
              </button>
              <button type="button" onClick={() => setActiveTab('all')}
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (activeTab === 'add' || activeTab === 'install' || activeTab === 'activate' || activeTab === 'available') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full text-center py-8">Loading...</div>
          ) : availablePackages.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">No packages available</div>
          ) : (
            availablePackages.map(pkg => (
              <div key={pkg.packageId} className="bg-white p-4 rounded shadow">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold">{pkg.name}</h3>
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{pkg.type}</span>
                  </div>
                  {pkg.isFeatured && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Featured</span>}
                </div>
                <p className="text-sm text-gray-500 mb-3">{pkg.description}</p>
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-lg font-bold">${pkg.pricing?.monthlyPrice || 0}</span>
                    <span className="text-sm text-gray-500">/mo</span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedPackage(pkg);
                      handleInstall(pkg.packageId);
                    }}
                    className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                  >
                    Install
                  </button>
                </div>
                {pkg.features?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-1">Features:</p>
                    <div className="flex flex-wrap gap-1">
                      {pkg.features.slice(0, 3).map((f, i) => (
                        <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-6">
          <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto my-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Browse Packages</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <div className="space-y-4">
              {availablePackages.map(pkg => {
                const isInstalled = packages.some(p => p.packageId?.packageId === pkg.packageId);
                return (
                  <div key={pkg.packageId} className={`p-4 border rounded ${isInstalled ? 'bg-gray-50' : 'bg-white'}`}>
                    <div className="flex justify-between">
                      <div>
                        <h3 className="font-semibold">{pkg.name}</h3>
                        <p className="text-sm text-gray-500">{pkg.description}</p>
                        <div className="flex gap-2 mt-2">
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{pkg.type}</span>
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{pkg.category}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">${pkg.pricing?.monthlyPrice || 0}<span className="text-sm font-normal">/mo</span></div>
                        {isInstalled ? (
                          <span className="text-green-600 text-sm">Installed</span>
                        ) : (
                          <button
                            onClick={() => handleInstall(pkg.packageId)}
                            className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                          >
                            Install
                          </button>
                        )}
                      </div>
                    </div>
                    {pkg.features?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {pkg.features.map((f, i) => (
                          <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}